import React, { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Mail, Phone, Building2, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await axios.get(`${API}/leads`);
      setLeads(data);
    } catch (e) {
      setErr("Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <main data-testid="admin-page" className="min-h-screen bg-[#0A0D14] text-white">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16">
        <div className="flex items-center justify-between">
          <div>
            <p className="uppercase text-xs tracking-[0.24em] text-emerald-400 font-semibold">
              Admin
            </p>
            <h1 className="font-display mt-2 text-3xl sm:text-4xl font-bold tracking-tight">
              Lead submissions
            </h1>
            <p className="mt-2 text-slate-400 text-sm">
              Total leads: <span className="text-white font-semibold" data-testid="admin-leads-count">{leads.length}</span>
            </p>
          </div>
          <button
            onClick={load}
            data-testid="admin-refresh"
            className="px-4 py-2.5 rounded-full glass border border-white/10 text-sm flex items-center gap-2 hover:border-emerald-500/40 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>

        <div className="mt-10 rounded-2xl glass border border-white/5 overflow-hidden">
          {loading ? (
            <div className="p-16 flex justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-emerald-400" />
            </div>
          ) : err ? (
            <div className="p-10 text-center text-red-400">{err}</div>
          ) : leads.length === 0 ? (
            <div className="p-16 text-center text-slate-500">
              No leads yet. Share your landing page to start capturing interest.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm" data-testid="admin-leads-table">
                <thead className="text-left text-xs uppercase tracking-widest text-slate-500 bg-white/[0.02]">
                  <tr>
                    <th className="px-6 py-4">Name</th>
                    <th className="px-6 py-4">Contact</th>
                    <th className="px-6 py-4">Business</th>
                    <th className="px-6 py-4">Message</th>
                    <th className="px-6 py-4">When</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l, i) => (
                    <tr
                      key={l.id}
                      data-testid={`admin-lead-row-${i}`}
                      className="border-t border-white/5 hover:bg-white/[0.02] transition-colors"
                    >
                      <td className="px-6 py-4 font-semibold">{l.name}</td>
                      <td className="px-6 py-4 text-slate-300">
                        <div className="flex items-center gap-2">
                          <Mail className="w-3.5 h-3.5 text-slate-500" /> {l.email}
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-slate-400">
                          <Phone className="w-3.5 h-3.5 text-slate-500" /> {l.phone}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-slate-300">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-3.5 h-3.5 text-slate-500" /> {l.business_type}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-slate-400 max-w-sm">{l.message || "—"}</td>
                      <td className="px-6 py-4 text-slate-500 whitespace-nowrap">
                        {new Date(l.created_at).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
